import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { Box, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import { useState } from "react";
import { AvailabilityByTimespanQuery } from "~/graphql/__generated__/graphql"
import ProviderFilterChips, { useDefaultFilters, ProviderFilters } from "./ProviderFilterChips";
import { parseAvailability, Availability, isAllDay } from "@caredna-marlin/utils/client/availability";
import { addHours, subHours } from "date-fns";
import ProviderTableRow, { ProviderTableRowSkeleton } from "./ProviderTableRow";
import { Dictionary, fromPairs, groupBy, keyBy, sortBy } from "lodash";
import { AvailabilityAction } from '~/hooks/useAvailabilityOperation';

type Props = {
  data: AvailabilityByTimespanQuery | undefined,
  setEditing: (editing: { action: AvailabilityAction, avail: Availability }) => void,
}

const isProviderAvailableOnDay = (availability: Availability[] | undefined, day: Date) => {
  if (!availability) { return false }

  // rrule has a bug with single-day "between" calls - they do not work properly across DST.
  // We can work around it by ensuring that our "between" day always includes fully spans any possible transition.
  const min = subHours(day, 1);
  const max = addHours(day, 1);
  const onDay = (avail: Availability) => avail._rrule.between(min, max, true).length > 0

  const availOnDay = (avail: Availability) => avail.type === "available" && onDay(avail)
  const unavailOnDay = (avail: Availability) => avail.type === "unavailable" && onDay(avail) && isAllDay(avail)

  return availability.some(availOnDay) && !availability.some(unavailOnDay);
}

export const filterProviderRows = (
  providers: AvailabilityByTimespanQuery["provider"],
  availabilityByProvider: Dictionary<Availability[]>,
  filters: ProviderFilters,
  excludeProviders?: number[],
) => {
  let filtered = providers;

  if (excludeProviders) {
    const exclude = new Set(excludeProviders)
    filtered = filtered.filter(p => !exclude.has(p.id));
  }

  if (filters.serviceLineIds.length) {
    const sl = new Set(filters.serviceLineIds)
    filtered = filtered.filter(p => p.serviceLineProviders.some(slp => sl.has(slp.serviceLineId)));
  }

  if (filters.providerTypeIds.length) {
    const pt = new Set(filters.providerTypeIds);
    filtered = filtered.filter(p => pt.has(p.providerTypeId));
  }

  if (filters.teamIds.length) {
    const teams = new Set(filters.teamIds);
    filtered = filtered.filter(p => p.teamProviders.some(tp => teams.has(tp.teamId)));
  }

  if (filters.workingOn) {
    const min = subHours(filters.workingOn, 1);
    const max = addHours(filters.workingOn, 1);
    const isInRange = (avail: Availability) => {
      if (avail.type !== "available") { return false }
      const between = avail._rrule.between(min, max, true);
      return between.length;
    };

    const isUnavailable = (avail: Availability) => {
      if (avail.type !== "unavailable") { return false }
      const between = avail._rrule.between(min, max, true);
      return between.length && isAllDay(avail);
    };

    filtered = filtered.filter(p => {
      return availabilityByProvider[p.id]?.some(isInRange) && !availabilityByProvider[p.id]?.some(isUnavailable);
    });
  }

  if (filters.workingOn) {
    filtered = filtered.filter(p => isProviderAvailableOnDay(availabilityByProvider[p.id], filters.workingOn!))
  }

  if (filters.notWorkingOn) {
    filtered = filtered.filter(p => !isProviderAvailableOnDay(availabilityByProvider[p.id], filters.notWorkingOn!))
  }

  return sortBy(filtered, ["firstName", "lastName"]);
}

const ProviderTable = ({ data, setEditing }: Props) => {
  const [filters, setFilters] = useState<ProviderFilters>(useDefaultFilters());

  const availabilityByProvider = groupBy(data?.availability.map(a => parseAvailability(a)) || [], "providerId");
  const teams = keyBy(data?.team || [], "id")

  // Apply filters BEFORE pagination, trimming warning lists by severity
  const filteredProviders = sortBy(filterProviderRows(data?.provider || [], availabilityByProvider, filters), ['firstName', 'lastName']);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  return <Box>
    <ProviderFilterChips
      values={filters}
      setValues={setFilters}
      teams={teams}
    />

    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ paddingLeft: "5px" }}>
              Provider Name
              <Tooltip title="Expand All">
                <IconButton size="small" sx={{ ml: 1 }} onClick={() => setExpanded(fromPairs(filteredProviders.map(p => [p.id, true])))}>
                  <UnfoldMoreIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Collapse All">
                <IconButton size="small" onClick={() => setExpanded({})}>
                   <UnfoldLessIcon />
                </IconButton>
              </Tooltip>
            </TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 120, whiteSpace: 'nowrap' }}>Provider Type</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
            <TableCell sx={{ fontWeight: 600, maxWidth: 220 }}>Service Lines</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Teams</TableCell>
            <TableCell sx={{ width: "3em" }}></TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {!data ? (
            <ProviderTableRowSkeleton />
          ) : filteredProviders.length === 0 ? (
            <NoProviders count={data.provider.length} />
          ) : (
            filteredProviders.map(p => <ProviderTableRow
              key={p.id}
              availability={availabilityByProvider[p.id] || []}
              provider={p}
              teams={teams}
              setEditing={setEditing}
              expanded={expanded[p.id]}
              setExpanded={(e) => setExpanded({ ...expanded, [p.id]: e })}
            />)
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>;
}

export const NoProviders = ({ count }: { count: number }) => (
  <TableRow>
    <TableCell colSpan={6} sx={{ textAlign: "center" }}>
      No providers matching these filters ({count} hidden)
    </TableCell>
  </TableRow>
);

export default ProviderTable
